import type { GoldUpsellGate } from "../lib/gold";
import type { ComponentType } from "react";
import type { MediaItem, SoundMode, View, ViewIdKind } from "@gooner/shared";
import type { FapBus, RegionRegistry } from "./types";
import type { SyncBus } from "./syncBus";
import type { RoomCtx } from "./room/useRoom";
import { InfinityStage } from "./InfinityStage";
import type { DrivenBy } from "./RegionTile";

/**
 * THE STAGE: what fills the glass under the bar.
 *
 * A view id's KIND already decides how the server resolves it (view-resolver) and how
 * the picker lists it (WhatsPlaying). This is the client's half of the same seam — a
 * kind can bring its own stage, and DisplayApp mounts it in place of the default one (a
 * view's authored regions, the map DisplayApp renders itself) with everything the
 * screen owns handed in: the region registry the crown and the keyboard read, the sound
 * verdict, the pause, the ledgers. A stage is a variant of a view, not a page of its
 * own, so the bar, the picker, the arrows, the goon session, the remote and the guide
 * all still work above it. Adding a variant is a file here and a row in STAGES.
 */
export interface StageProps {
  /** The view id this stage is playing — a stage may serve several ids of its kind
   *  (Random Scroll and Keyword Scroll are one stage, two feeds). */
  viewId: string;
  /** The resolved view. */
  view: View;
  /** The Goon Room this screen is in; null off a room. */
  room: RoomCtx | null;
  /** FOLLOW MODE: a room member's tiles are driven off the room's bus (the host's
   *  reveals) and the stage deals nothing of its own; null for the host and off a room. */
  follow: DrivenBy | null;
  /** Live region controllers, filled by every RegionPlayer the stage mounts. */
  registry: RegionRegistry;
  /** Per-region fap callbacks, filled by every RegionFapOverlay. */
  fapBus: FapBus;
  syncBus: SyncBus;
  paused: boolean;
  /** The screen's four-valued sound mode — the senior owner of sound (see DisplayApp). */
  soundMode: SoundMode;
  /** The master fader, 0..1. */
  master: number;
  portrait: boolean;
  signedIn: boolean;
  onAutoMuted: () => void;
  onView: (item: MediaItem) => void;
  /** A press asked for sound while the screen was muted: the stage cannot flip the
   *  screen's mode itself, so it asks. The host answers by entering a sounding mode. */
  /** Auto-scroll's wait between walls, seconds; 0 = off (the ☰ row's setting). */
  autoScrollSec: number;
  /** Open the Premium pitch in this gate's voice — the host owns the dialog. */
  onGold: (gate: GoldUpsellGate) => void;
  /** Point this screen at another view — the host owns the screen. */
  onPoint: (viewId: string) => void;
}

// A ROOM IS NOT A STAGE (2026-09-19): a room is the host's screen, mirrored — the host
// runs their real view through the same paths as /screen, a member the same stage in
// follow mode — so the only kind with a stage of its own is the scroll.
export const STAGES: Partial<Record<ViewIdKind, ComponentType<StageProps>>> = {
  infinity: InfinityStage,
};
